import type { InventoryInformationEditView } from "@slfinrtl/ui-bff-routes"
import { vehicleTypeMessages } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { RadioOption } from "src/components/form"
import { useConfig } from "src/hooks"
import { inventoryAndIntakeMessages } from "../common/inventoryAndIntake.messages"
import { useMinimumSubsidizedUsageTime } from "./useMinimumSubsidizedUsageTime"

export const useVehicleTypeOptions = (
	vmsDataSource: "PVMS" | "ONE_VMS" | undefined,
	view: InventoryInformationEditView,
): RadioOption[] => {
	const { formatMessage } = useIntl()
	const { allVehicleTypesAvailable } = useConfig()
	const { subsidizedUsageCannotBeTerminated } = useMinimumSubsidizedUsageTime(view)

	const pvmsDisabledHint = useMemo(
		() =>
			vmsDataSource
				? formatMessage(commonMessages.disabledForVmsVehicle, {
						value: vmsDataSource === "ONE_VMS" ? "OneVMS" : "PVMS",
					})
				: undefined,
		[vmsDataSource, formatMessage],
	)

	const subsidizedUsageHint = useMemo(
		() =>
			subsidizedUsageCannotBeTerminated
				? formatMessage(inventoryAndIntakeMessages.minimumUsageTimeNotReached)
				: undefined,
		[formatMessage, subsidizedUsageCannotBeTerminated],
	)

	const generalDisabledHint = useMemo(
		() => pvmsDisabledHint ?? subsidizedUsageHint,
		[pvmsDisabledHint, subsidizedUsageHint],
	)

	const newCarDisabledHint: string | undefined = useMemo(() => {
		return view.values.inventoryAndIntake.vehicleType === "CUSTOMER_REPLACEMENT_CAR" ||
			view.values.inventoryAndIntake.vehicleType === "COMPANY_CAR" ||
			view.values.inventoryAndIntake.vehicleType === "DEMO_CAR"
			? formatMessage(inventoryAndIntakeMessages.disabledForInUseVehicle)
			: undefined
	}, [formatMessage, view.values.inventoryAndIntake.vehicleType])

	const currentVehicleType = useMemo(
		() => view.values.inventoryAndIntake.vehicleType,
		[view.values.inventoryAndIntake.vehicleType],
	)

	return [
		{
			disabledHint: newCarDisabledHint ?? generalDisabledHint,
			label: formatMessage(vehicleTypeMessages.NEW_CAR),
			value: "NEW_CAR",
		},
		{
			disabledHint: generalDisabledHint,
			label: formatMessage(vehicleTypeMessages.USED_CAR),
			value: "USED_CAR",
		},
		{
			disabledHint:
				generalDisabledHint ??
				(!allVehicleTypesAvailable && currentVehicleType !== "DEMO_CAR"
					? formatMessage(commonMessages.actionNotAvailable)
					: undefined),
			label: formatMessage(vehicleTypeMessages.DEMO_CAR),
			value: "DEMO_CAR",
		},
		{
			disabledHint:
				generalDisabledHint ??
				(!allVehicleTypesAvailable && currentVehicleType !== "CUSTOMER_REPLACEMENT_CAR"
					? formatMessage(commonMessages.actionNotAvailable)
					: undefined),
			label: formatMessage(vehicleTypeMessages.CUSTOMER_REPLACEMENT_CAR),
			value: "CUSTOMER_REPLACEMENT_CAR",
		},
		{
			disabledHint:
				generalDisabledHint ??
				(!allVehicleTypesAvailable && currentVehicleType !== "COMPANY_CAR"
					? formatMessage(commonMessages.actionNotAvailable)
					: undefined),
			label: formatMessage(vehicleTypeMessages.COMPANY_CAR),
			value: "COMPANY_CAR",
		},
		{
			disabledHint: generalDisabledHint,
			label: formatMessage(vehicleTypeMessages.COMMISSION_CAR),
			value: "COMMISSION_CAR",
		},
	]
}
